// Turn a stream of GPS points into a day's STOPS and TRIPS — the whole "Maps Timeline"
// trick from raw GPS alone. A stop = you stayed put a while; a trip = the moving stretch
// between stops (its points are the route). Driving falls out of GPS `speed`. Pure — no I/O.
//
// point: { lat, lng, t (ms), speed? (m/s) }

import { localDateStr } from './time.js';

const R = 6371000;
const toRad = (d) => (d * Math.PI) / 180;
export function distM(a, b) {
  if (!a || !b) return Infinity;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
const pathDist = (pts) => pts.reduce((sum, p, i) => (i === 0 ? 0 : sum + distM(pts[i - 1], p)), 0);
// The middle of a parked cluster: the median, so one wild fix can't drag it off the spot.
const med = (xs) => { const v = [...xs].sort((a, b) => a - b); const m = v.length >> 1; return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2; };
const medianCentre = (pts) => ({ lat: med(pts.map((p) => p.lat)), lng: med(pts.map((p) => p.lng)) });

/**
 * @param {Array} points  {lat,lng,t,speed?}
 * @param {Object} opts    { moveSpeed=1.4 m/s, minStopMs=4min, maxStopRadius=70m, maxGapMs=20min }
 * @returns {{ stops, trips }} stops:{lat,lng,start,end,durationMs,gap?}  trips:{start,end,distanceM,drive,points:[{lat,lng}]}
 */
export function segmentDay(points = [], opts = {}) {
  const moveSpeed = opts.moveSpeed ?? 1.4;        // ~5 km/h — walking pace = "moving"
  const minStopMs = opts.minStopMs ?? 4 * 60000;  // dwell this long to count as a real stop
  const maxStopRadius = opts.maxStopRadius ?? 70;  // a still-cluster wider than this isn't one stop
  const maxGapMs = opts.maxGapMs ?? 20 * 60000;    // a recording gap longer than this = an implicit stop

  const pts = points.filter((p) => p && typeof p.lat === 'number' && typeof p.lng === 'number' && p.t).sort((a, b) => a.t - b.t);
  if (pts.length < 2) return { stops: [], trips: [] };

  const stops = [];
  const trips = [];
  let tripBuf = [];
  const flushTrip = () => {
    if (tripBuf.length > 1) trips.push({ start: tripBuf[0].t, end: tripBuf[tripBuf.length - 1].t, distanceM: Math.round(pathDist(tripBuf)), drive: tripBuf.some((p) => (p.speed || 0) > 5), points: tripBuf.map((p) => ({ lat: p.lat, lng: p.lng })) });
    tripBuf = [];
  };

  // A long pause in recording (phone off / app closed / parked with GPS asleep) is itself a stop —
  // and it MUST break trips, or an overnight gap glues the whole day into one nonsensical mega-trip.
  // Split the stream into contiguous chunks at those gaps and emit the gap as a stop between them.
  const chunks = [];
  const gaps = [];
  let chunk = [pts[0]];
  for (let i = 1; i < pts.length; i++) {
    if (pts[i].t - pts[i - 1].t > maxGapMs) { chunks.push(chunk); gaps.push([pts[i - 1], pts[i]]); chunk = [pts[i]]; }
    else chunk.push(pts[i]);
  }
  chunks.push(chunk);

  // Segment one contiguous chunk into stops + trips (the original algorithm), per-chunk.
  const segChunk = (cp) => {
    if (cp.length < 2) { tripBuf.push(...cp); return; }
    // 1) Classify each point moving/still — by GPS speed if present, else displacement rate.
    const cls = cp.map((p, i) => {
      let v = typeof p.speed === 'number' && p.speed >= 0 ? p.speed : null;
      if (v == null && i > 0) { const dt = (p.t - cp[i - 1].t) / 1000 || 1; v = distM(cp[i - 1], p) / dt; }
      return (v ?? 0) > moveSpeed ? 'move' : 'still';
    });
    // 2) Group consecutive same-class runs.
    const runs = [];
    let cur = { type: cls[0], a: 0 };
    for (let i = 1; i < cp.length; i++) { if (cls[i] !== cur.type) { cur.b = i - 1; runs.push(cur); cur = { type: cls[i], a: i }; } }
    cur.b = cp.length - 1; runs.push(cur);
    // 3) Long still-runs (tight enough) = stops; everything else is trip material.
    for (const run of runs) {
      const seg = cp.slice(run.a, run.b + 1);
      const dur = seg[seg.length - 1].t - seg[0].t;
      // Tight = nearly every fix within the radius of the median spot — a parked phone throws the odd fix 100 m
      // off, and one of those used to turn a 49-minute stop (and its jitter "distance") into driving.
      const c = medianCentre(seg);
      const tight = seg.filter((p) => distM(c, p) <= maxStopRadius).length >= Math.ceil(seg.length * 0.9);
      if (run.type === 'still' && dur >= minStopMs && tight) {
        flushTrip();
        stops.push({ lat: c.lat, lng: c.lng, start: seg[0].t, end: seg[seg.length - 1].t, durationMs: dur });
      } else {
        tripBuf.push(...seg); // short stills (traffic lights) get absorbed into the trip
      }
    }
  };

  chunks.forEach((cp, ci) => {
    segChunk(cp);
    if (ci < gaps.length) {
      // Close the trip on either side of the gap and record the gap as a stop at the last known spot.
      flushTrip();
      const [a, b] = gaps[ci];
      stops.push({ lat: a.lat, lng: a.lng, start: a.t, end: b.t, durationMs: b.t - a.t, gap: true });
    }
  });
  flushTrip();

  stops.sort((x, y) => x.start - y.start);
  trips.sort((x, y) => x.start - y.start);
  return { stops, trips };
}

/**
 * GPS points grouped by the local day each was RECORDED on ({ 'YYYY-MM-DD': points[] }, time order). Android can
 * hold a background recorder's fixes and hand them over hours later — filing them under the day they arrive put
 * Sunday's drive on Monday.
 */
export function trackPointsByDay(points = []) {
  const out = {};
  for (const p of points) {
    if (!p || !Number.isFinite(p.t)) continue;
    const d = localDateStr(p.t);
    (out[d] = out[d] || []).push(p);
  }
  for (const d of Object.keys(out)) out[d].sort((a, b) => a.t - b.t);
  return out;
}
